import { createSlice } from '@reduxjs/toolkit'
import { authErrors, unknownError } from 'commons/messages'
import type { CodeResponseStorage } from 'container/ForgetPassword/components/types/responses'
import { AES } from 'crypto-js'
import { toast } from 'react-toastify'
import { sendCodeRequest } from 'services/forgetPassword'
import { login } from 'services/login'
import type {
  AccessDataInterface,
  DataAuthRequest,
  UserPreferences,
} from 'services/login/interfaces'
import { setLoginValidation } from 'store/StructuralContext/structuralSlice'
import type { AppThunk } from 'store/store'

import type { authSliceType, ErrorMessage } from './Types/authSliceType'

const initialState: authSliceType = {
  authData: {
    ds_emai: '',
    ds_senh: '',
    nr_cnpj_cpf: 0,
  },
  cd_usua: JSON.parse(localStorage.getItem('cd_usua') || '{}').cd_usua || '',
  email: '',
  error: {
    description: '',
    title: '',
  },
  isLoading: false,
  loginTries: 0,
  offices: JSON.parse(localStorage.getItem('offices') || '[]'),
  permission: JSON.parse(localStorage.getItem('permission') || '[]'),
  preferences: JSON.parse(localStorage.getItem('preferences') || '{}'),
  roles: JSON.parse(localStorage.getItem('roles') || '{}').roles || '',
  token: JSON.parse(localStorage.getItem('token') || '{}'),
}

export const authSlice = createSlice({
  initialState,
  name: 'auth',
  reducers: {
    clearState: () => ({
      authData: {
        ds_emai: '',
        ds_senh: '',
        nr_cnpj_cpf: 0,
      },
      cd_usua: '',
      email: '',
      error: {
        description: '',
        title: '',
      },
      isLoading: false,
      loginTries: 0,
      offices: [],
      permission: [],
      preferences: {} as UserPreferences,
      roles: '',
      token: {},
    }),
    loginConfirm: (state, { payload }) => ({
      ...state,
      authData: payload.authData,
      email: payload.email,
      isLoading: false,
    }),
    loginFail: (state, { payload }) => ({
      ...state,
      email: payload.email,
      error: payload.error,
      isLoading: false,
      loginTries: payload.tries,
    }),
    loginRequest: (state) => ({
      ...state,
      error: {
        description: '',
        title: '',
      },
      isLoading: true,
    }),
    loginSuccess: (state, { payload }) => ({
      ...state,
      cd_usua: payload.cd_usua,
      isLoading: false,
      offices: payload.offices,
      permission: payload.permission,
      preferences: payload.preferences,
      roles: payload.roles,
      token: payload.token,
    }),
    setEmail: (state, { payload }) => ({
      ...state,
      email: payload,
    }),
    setLoading: (state, { payload }) => ({
      ...state,
      isLoading: payload,
    }),
  },
})

const { actions, reducer } = authSlice
export const {
  setLoading,
  loginRequest,
  loginConfirm,
  setEmail,
  loginSuccess,
  loginFail,
  clearState,
} = actions
export default reducer

export const handleLogout = (): AppThunk => (dispatch) => {
  localStorage.clear()
  dispatch(clearState())
}

export const handleAuth =
  (data: DataAuthRequest, successCallback: () => void): AppThunk =>
  async (dispatch) => {
    dispatch(loginRequest())

    const accessData = await login(data)

    if (accessData && accessData.Token) {
      const cd_usua = accessData.Codigo ?? data.cd_usua ?? ''
      const rolesEncrypted = AES.encrypt(accessData.RetornoWs.UserRole, 'user-roles-key').toString()
      const ds_emai = accessData.Email ?? data.ds_emai ?? ''

      setAuthStorage(cd_usua, rolesEncrypted, accessData, ds_emai)
      dispatch(
        loginSuccess({
          cd_usua,
          offices: accessData.RetornoWs.EscritorioDiretor.map((office) => office.cd_escr_vend),
          permission: accessData.RetornoWs.UserPermissions,
          preferences: accessData.RetornoWs.Preferences,
          roles: rolesEncrypted,
          token: accessData.Token,
        }),
      )
      successCallback()
    } else if (accessData.RetornoWs.RetCod === '0') {
      dispatch(loginConfirm({ authData: data, email: accessData.Email }))
      dispatch(setLoginValidation(true))
      toast.success('Código enviado com sucesso!')
    } else if (accessData.RetornoWs.RetCod === '99') {
      dispatch(loginConfirm({ authData: data, email: accessData.Email }))
      dispatch(resendAuthCode({ ...data, fl_renv_cod: 'S' }))
      dispatch(setLoginValidation(true))
    } else {
      const { Email: email, TentativasAcesso: tries } = accessData
      const error = handleErrorMessage(accessData)
      dispatch(loginFail({ email, error, tries }))
    }
  }

export const resendAuthCode =
  (data: DataAuthRequest): AppThunk =>
  async () => {
    const accessData = await login(data)

    if (accessData.RetornoWs.RetCod === '0') {
      toast.success('Código enviado com sucesso!')
    } else {
      toast.error('Erro ao enviar código. Tente novamente.')
    }
  }

export const handleConfirmAuth =
  (data: DataAuthRequest, successCallback: () => void, errorCallback: () => void): AppThunk =>
  async (dispatch) => {
    dispatch(loginRequest())

    const accessData = await login(data)

    if (accessData && accessData.Token) {
      const cd_usua = accessData.Codigo ?? data.cd_usua ?? ''
      const rolesEncrypted = AES.encrypt(accessData.RetornoWs.UserRole, 'user-roles-key').toString()
      const ds_emai = accessData.Email ?? data.ds_emai ?? ''

      setAuthStorage(cd_usua, rolesEncrypted, accessData, ds_emai)
      dispatch(
        loginSuccess({
          cd_usua,
          offices: accessData.RetornoWs.EscritorioDiretor.map((office) => office.cd_escr_vend),
          permission: accessData.RetornoWs.UserPermissions,
          preferences: accessData.RetornoWs.Preferences,
          roles: rolesEncrypted,
          token: accessData.Token,
        }),
      )
      successCallback()
      dispatch(setLoginValidation(false))
    } else {
      const { Email: email, TentativasAcesso: tries } = accessData
      const error = handleErrorMessage(accessData)
      dispatch(loginFail({ email, error, tries }))
      errorCallback()
    }
  }

export const resetPasswordRequest =
  (email: string, successCallback: () => void): AppThunk =>
  async (dispatch) => {
    dispatch(setLoading(true))
    try {
      const response = await sendCodeRequest({
        ds_emai: email,
      })
      const composeStorage: CodeResponseStorage = {
        email: email,
        id_usua: response.id_usua,
        nm_usua: response?.nm_usua || '',
        return_chav: response.return_chav,
        return_code: response.return_code,
      }
      localStorage.setItem('resetPassword', JSON.stringify(composeStorage))
      successCallback()
    } catch (error) {
      toast.error('Erro ao solicitar redefinição de senha.')
    } finally {
      dispatch(setLoading(false))
    }
  }

const setAuthStorage = (
  cd_usua: string,
  roles: string,
  accessData: AccessDataInterface,
  ds_emai: string,
) => {
  localStorage.setItem('token', JSON.stringify({ ...accessData.Token }))
  localStorage.setItem('permission', JSON.stringify({ ...accessData.RetornoWs.UserPermissions }))
  localStorage.setItem('cd_usua', JSON.stringify({ cd_usua }))
  localStorage.setItem('ds_emai', JSON.stringify({ ds_emai }))
  localStorage.setItem('roles', JSON.stringify({ roles }))
  localStorage.setItem('preferences', JSON.stringify(accessData.RetornoWs.Preferences))
  localStorage.setItem(
    'offices',
    JSON.stringify(accessData.RetornoWs.EscritorioDiretor.map((office) => office.cd_escr_vend)),
  )
}

const handleErrorMessage = (accessData: AccessDataInterface): ErrorMessage => {
  const { RetCod } = accessData.RetornoWs

  return (RetCod && authErrors[RetCod]) || unknownError()
}
